// src/app/time-attendance/scheduling/services/scheduling.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

import {
  ApiPagedResponse,
  ApiResponse,
  CreateScheduleGroupRequest,
  CurrentUser,
  PatchScheduleGroupRequest,
  ScheduleGroupDetails,
  ScheduleGroupListItem,
  ShiftListItem,
  GetUsersQuery,
  UserFilterValuesResponse,
  UserListItem,
} from '../models/scheduling.model';

@Injectable({ providedIn: 'root' })
export class SchedulingService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /* =============================
     Schedule Groups
     ============================= */
  // scheduling.service.ts
  getScheduleGroups(query?: { page?: number; pageSize?: number }) {
    const params = new HttpParams()
      .set('pageNumber', String(query?.page ?? 1))
      .set('pageSize', String(query?.pageSize ?? 6));

    return this.http.get<ApiPagedResponse<ScheduleGroupListItem>>(
      `${this.baseUrl}/api/schedule-groups`,
      { params },
    );
  }

  getScheduleGroupById(id: number): Observable<ApiResponse<ScheduleGroupDetails>> {
    return this.http.get<ApiResponse<ScheduleGroupDetails>>(
      `${this.baseUrl}/api/schedule-groups/${id}`,
    );
  }

  createScheduleGroup(
    body: CreateScheduleGroupRequest,
  ): Observable<ApiResponse<ScheduleGroupDetails>> {
    return this.http.post<ApiResponse<ScheduleGroupDetails>>(
      `${this.baseUrl}/api/schedule-groups`,
      body,
    );
  }

  patchScheduleGroupName(id: number, name: string): Observable<ApiResponse<ScheduleGroupDetails>> {
    const body: PatchScheduleGroupRequest = { name };
    return this.http.patch<ApiResponse<ScheduleGroupDetails>>(
      `${this.baseUrl}/api/schedule-groups/${id}`,
      body,
    );
  }

  removeScheduleWithDay(
    scheduleGroupId: number,
    scheduleWithDayId: number,
  ): Observable<ApiResponse<unknown>> {
    return this.http.delete<ApiResponse<unknown>>(
      `${this.baseUrl}/api/schedule-groups/${scheduleGroupId}/remove-schedule-with-day/${scheduleWithDayId}`,
    );
  }

  /* =============================
     Assign schedule to employees
     ============================= */

  assignScheduleGroupToEmployees(
    scheduleGroupId: number,
    departmentId: number,
    employeeIds: number[],
  ): Observable<ApiResponse<unknown>> {
    return this.http.patch<ApiResponse<unknown>>(
      `${this.baseUrl}/api/schedule-groups/${scheduleGroupId}/department/${departmentId}/assign-to-employees`,
      employeeIds,
    );
  }

  /* =============================
     Supporting data
     ============================= */

  getShifts(opts?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Observable<ApiPagedResponse<ShiftListItem>> {
    let params = new HttpParams();

    if (opts?.page != null) params = params.set('page', String(opts.page));
    if (opts?.pageSize != null) params = params.set('pageSize', String(opts.pageSize));
    if (opts?.search) params = params.set('search', opts.search);

    const hasParams = opts?.page != null || opts?.pageSize != null || !!opts?.search;

    return this.http.get<ApiPagedResponse<ShiftListItem>>(
      `${this.baseUrl}/api/shifts`,
      hasParams ? { params } : undefined,
    );
  }

  /**
   * NOTE: This endpoint is not wrapped in { status, data } per your example.
   */
  getCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${this.baseUrl}/api/account/get-current-user`);
  }

  getUserFilterValues(): Observable<UserFilterValuesResponse> {
    return this.http.get<UserFilterValuesResponse>(`${this.baseUrl}/api/users/filter-values`);
  }

  getUsers(query?: GetUsersQuery): Observable<ApiPagedResponse<UserListItem>> {
  let params = new HttpParams();

  const setIf = (key: string, val: unknown) => {
    if (val === null || val === undefined) return;
    const s = String(val).trim();
    if (!s) return;
    params = params.set(key, s);
  };

  setIf('page', query?.page ?? 1);
  setIf('pageSize', query?.pageSize ?? 25);
  setIf('search', query?.search);

  setIf('companyId', query?.companyId);
  setIf('departmentId', query?.departmentId);
  setIf('workerTypeId', query?.workerTypeId);
  setIf('shiftId', query?.shiftId);
  setIf('payGroupId', query?.payGroupId);
  setIf('workOrderNumberId', query?.workOrderNumberId);

  params = params.set('employmentStatus', 'Active');

  return this.http.get<ApiPagedResponse<UserListItem>>(
    `${this.baseUrl}/api/users`,
    { params }
  );
}
}
